import { invisiblyApi } from '../../api/invisibly-api';
import { InvisiblyApiResponse } from '../../../services/api/api-response.types';
import {
  MartBreakdownRow,
  MartFilterOption,
  MartReportFilters,
  MartReportMetrics,
  MartTimeSeriesPoint,
} from './mart-reporting.types';

type MartReportRequest = Partial<MartReportFilters> & {
  report_type: 'metrics' | 'timeseries' | 'geo' | 'domain';
};

const buildMartReportBody = (data: MartReportRequest) => ({
  start_date: data.start_date,
  end_date: data.end_date,
  report_type: data.report_type,
  source: data.source || undefined,
  seats: data.seats?.length ? data.seats : undefined,
  advertiser_ids: data.advertiser_ids?.length ? data.advertiser_ids : undefined,
  campaign_ids: data.campaign_ids?.length ? data.campaign_ids : undefined,
  line_item_ids: data.line_item_ids?.length ? data.line_item_ids : undefined,
});

const martReportingEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getMartSources: builder.query<MartFilterOption[], void>({
      query: () => ({
        method: 'get',
        url: 'b2b/mart-reporter/filter/sources',
      }),
      transformResponse(response: InvisiblyApiResponse<{ sources: MartFilterOption[] }>) {
        return response.item.sources;
      },
    }),
    getMartSeats: builder.query<MartFilterOption[], { source: string }>({
      query: ({ source }) => ({
        method: 'get',
        url: `b2b/mart-reporter/filter/sources/${source}/seats`,
      }),
      transformResponse(response: InvisiblyApiResponse<{ seats: MartFilterOption[] }>) {
        return response.item.seats;
      },
    }),
    getMartAdvertisers: builder.query<MartFilterOption[], { source: string; seats?: string[] }>({
      query: ({ source, seats }) => ({
        method: 'get',
        url: `b2b/mart-reporter/filter/sources/${source}/advertisers`,
        params: seats?.length ? { seats: seats.join(',') } : undefined,
      }),
      transformResponse(response: InvisiblyApiResponse<{ advertisers: MartFilterOption[] }>) {
        return response.item.advertisers;
      },
    }),
    getMartCampaigns: builder.query<
      MartFilterOption[],
      { source: string; seats?: string[]; advertiser_ids?: string[] }
    >({
      query: ({ source, seats, advertiser_ids }) => ({
        method: 'get',
        url: `b2b/mart-reporter/filter/sources/${source}/campaigns`,
        params: {
          ...(seats?.length ? { seats: seats.join(',') } : {}),
          ...(advertiser_ids?.length ? { advertiser_ids: advertiser_ids.join(',') } : {}),
        },
      }),
      transformResponse(response: InvisiblyApiResponse<{ campaigns: MartFilterOption[] }>) {
        return response.item.campaigns;
      },
    }),
    getMartLineItems: builder.query<
      MartFilterOption[],
      { source: string; seats?: string[]; advertiser_ids?: string[]; campaign_ids?: string[] }
    >({
      query: ({ source, seats, advertiser_ids, campaign_ids }) => ({
        method: 'get',
        url: `b2b/mart-reporter/filter/sources/${source}/line-items`,
        params: {
          ...(seats?.length ? { seats: seats.join(',') } : {}),
          ...(advertiser_ids?.length ? { advertiser_ids: advertiser_ids.join(',') } : {}),
          ...(campaign_ids?.length ? { campaign_ids: campaign_ids.join(',') } : {}),
        },
      }),
      transformResponse(response: InvisiblyApiResponse<{ line_items: MartFilterOption[] }>) {
        return response.item.line_items;
      },
    }),
    getMartReportMetrics: builder.mutation<MartReportMetrics, MartReportFilters>({
      query: (data) => ({
        method: 'POST',
        url: 'b2b/mart-reporter',
        data: buildMartReportBody({ ...data, report_type: 'metrics' }),
      }),
      transformResponse(response: InvisiblyApiResponse<MartReportMetrics>) {
        return response.item;
      },
    }),
    getMartTimeSeries: builder.mutation<MartTimeSeriesPoint[], MartReportFilters>({
      query: (data) => ({
        method: 'POST',
        url: 'b2b/mart-reporter',
        data: buildMartReportBody({ ...data, report_type: 'timeseries' }),
      }),
      transformResponse(response: InvisiblyApiResponse<MartTimeSeriesPoint[]>) {
        return response.item;
      },
    }),
    getMartGeoBreakdown: builder.mutation<
      MartBreakdownRow[],
      MartReportFilters & { field?: string; metric?: string; amount?: number }
    >({
      query: ({ field = 'country_code', metric = 'impressions', amount = 10, ...data }) => ({
        method: 'POST',
        url: 'b2b/mart-reporter',
        data: buildMartReportBody({ ...data, report_type: 'geo' }),
        params: { field, metric, amount },
        handleErrorGlobally: false,
      }),
      transformResponse(response: InvisiblyApiResponse<MartBreakdownRow[]>) {
        return response.item.map((item) => ({
          ...item,
          key: String(
            item.key ??
              item.country_code ??
              item.region ??
              item.city ??
              item.dma ??
              item.zipcode ??
              item.zip ??
              '',
          ),
        }));
      },
    }),
    getMartDomainBreakdown: builder.mutation<
      MartBreakdownRow[],
      MartReportFilters & { field?: string; metric?: string; amount?: number }
    >({
      query: ({ field = 'domain', metric = 'impressions', amount = 10, ...data }) => ({
        method: 'POST',
        url: 'b2b/mart-reporter',
        data: buildMartReportBody({ ...data, report_type: 'domain' }),
        params: { field, metric, amount },
        handleErrorGlobally: false,
      }),
      transformResponse(response: InvisiblyApiResponse<MartBreakdownRow[]>) {
        return response.item.map((item) => ({
          ...item,
          key: String(item.key ?? item.domain ?? item.app ?? item.app_id ?? item.app_name ?? ''),
        }));
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetMartSourcesQuery,
  useGetMartSeatsQuery,
  useGetMartAdvertisersQuery,
  useGetMartCampaignsQuery,
  useGetMartLineItemsQuery,
  useGetMartReportMetricsMutation,
  useGetMartTimeSeriesMutation,
  useGetMartGeoBreakdownMutation,
  useGetMartDomainBreakdownMutation,
} = martReportingEndpoints;
